(() => {
  'use strict';

  const DESKTOP_VIDEO_URL = 'https://cdn.shopify.com/videos/c/o/v/d842b527b4e140b8bdf2d0e0869c4c77.webm';
  const MOBILE_ANIMATED_URL = 'https://cdn.shopify.com/s/files/1/0556/1342/3650/files/choices-globe-mobile-animated-hd.webp?v=1782935389';
  const POSTER_URL = 'https://cdn.shopify.com/s/files/1/0556/1342/3650/files/choices-globe-poster-solid.png';
  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktopHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initializedHeaders = new WeakSet();
  const initializedPanels = new WeakSet();
  const IDLE_START = 0.12;
  const IDLE_END = 2.8;
  const PULSE_END = 11.45;

  const isMobile = () => mobileQuery.matches;

  function safelyPlay(video) {
    if (document.hidden) return;
    const promise = video.play();
    if (promise?.catch) promise.catch(() => {});
  }

  function createVideo(className, loop = true, preload = 'none') {
    const video = document.createElement('video');
    video.className = className;
    video.src = DESKTOP_VIDEO_URL;
    video.poster = POSTER_URL;
    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = loop;
    video.playsInline = true;
    video.preload = preload;
    video.disablePictureInPicture = true;
    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('aria-hidden', 'true');
    video.tabIndex = -1;
    return video;
  }

  function createAnimatedImage(className) {
    const image = document.createElement('img');
    image.className = className;
    image.src = MOBILE_ANIMATED_URL;
    image.alt = '';
    image.decoding = 'async';
    image.setAttribute('aria-hidden', 'true');
    image.draggable = false;
    return image;
  }

  function scheduleHeavyMedia(callback) {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
    } else if (document.readyState === 'complete') {
      window.setTimeout(callback, 0);
    } else {
      window.addEventListener('load', callback, { once: true });
    }
  }

  function initHeader(root = document) {
    root.querySelectorAll?.('.header__heading-logo-wrapper').forEach((wrapper) => {
      if (initializedHeaders.has(wrapper)) return;
      initializedHeaders.add(wrapper);
      wrapper.classList.add('choices-globe-enabled');
      wrapper.querySelectorAll('.choices-header-globe').forEach((node) => node.remove());

      scheduleHeavyMedia(() => {
        if (!wrapper.isConnected) return;

        if (isMobile()) {
          const image = createAnimatedImage('choices-header-globe choices-header-globe--mobile-image');
          wrapper.appendChild(image);
          wrapper.classList.add('is-video-ready', 'is-mobile-image-ready');
          return;
        }

        if (reducedMotion.matches) {
          wrapper.classList.add('is-video-ready');
          return;
        }

        const video = createVideo('choices-header-globe choices-header-globe--desktop', false, 'none');
        let mode = 'idle';

        function returnToIdle() {
          mode = 'idle';
          video.playbackRate = 1;
          try { video.currentTime = IDLE_START; } catch (_error) {}
          safelyPlay(video);
        }

        function triggerPulse() {
          if (mode === 'pulse') return;
          mode = 'pulse';
          video.playbackRate = 1.08;
          try { video.currentTime = IDLE_END; } catch (_error) {}
          safelyPlay(video);
        }

        video.addEventListener('loadeddata', () => {
          wrapper.classList.add('is-video-ready');
          returnToIdle();
        }, { once: true });

        video.addEventListener('timeupdate', () => {
          if (mode === 'idle' && video.currentTime >= IDLE_END) {
            try { video.currentTime = IDLE_START; } catch (_error) {}
          } else if (mode === 'pulse' && video.currentTime >= PULSE_END) {
            returnToIdle();
          }
        });

        if (desktopHover.matches) {
          wrapper.closest('a')?.addEventListener('mouseenter', triggerPulse, { passive: true });
        }

        wrapper.appendChild(video);
        video.load();
        safelyPlay(video);
      });
    });
  }

  function hideNativeSpinner(panel) {
    panel.querySelectorAll('.choices-try-on__spinner, .loading__spinner, .spinner, [data-loading-spinner], [role="progressbar"]').forEach((node) => {
      node.hidden = true;
      node.style.display = 'none';
      node.setAttribute('aria-hidden', 'true');
    });
  }

  function initLoadingPanel(panel) {
    if (!panel || initializedPanels.has(panel)) return;
    initializedPanels.add(panel);
    let loader = null;

    function ensureLoader() {
      if (loader) return loader;
      loader = isMobile()
        ? createAnimatedImage('choices-try-on__globe-loader choices-try-on__globe-loader--mobile-image')
        : createVideo('choices-try-on__globe-loader choices-try-on__globe-loader--desktop', true, 'none');
      loader.hidden = true;
      const loadingBox = panel.querySelector('.choices-try-on__loading') || panel;
      const message = loadingBox.querySelector('h3, [data-loading-message], p');
      if (message) message.insertAdjacentElement('beforebegin', loader);
      else loadingBox.prepend(loader);
      return loader;
    }

    function sync() {
      const active = !panel.hidden;
      if (!active) {
        if (loader) {
          loader.hidden = true;
          if (loader.tagName === 'VIDEO') {
            loader.pause();
            try { loader.currentTime = 0; } catch (_error) {}
          }
        }
        return;
      }

      hideNativeSpinner(panel);
      const node = ensureLoader();
      node.hidden = false;
      if (node.tagName === 'VIDEO') {
        node.load();
        safelyPlay(node);
      }
    }

    new MutationObserver(sync).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    sync();
  }

  function initTryOnLoaders(root = document) {
    root.querySelectorAll?.('choices-try-on [data-panel="loading"]').forEach(initLoadingPanel);
  }

  function start(root = document) {
    initHeader(root);
    initTryOnLoaders(root);
  }

  document.addEventListener('shopify:section:load', (event) => start(event.target));
  document.addEventListener('choices:try-on-ready', () => initTryOnLoaders());
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll('video.choices-header-globe, video.choices-try-on__globe-loader').forEach((video) => {
      if (document.hidden) video.pause();
      else if (!video.hidden) safelyPlay(video);
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  } else {
    start();
  }
})();

(() => {
  'use strict';

  const standardHandles = new Set([
    'choices-bbc-t-shirt','choices-chickies-t-shirt','chopped-blk-t-shirt','chopped-org-t-shirt',
    'choices-chopped-zombie-t-shirt','dodge-this-t-shirt','ernie-banks-t-shirt','hd-gun-metal-t-shirt',
    'hd-org-chopped-t-shirt-11','i-love-cga-t-shirt','choices-juneteenth-001-t-shirt','lifes-a-btch-t-shirt',
    'choices-midwest-t-shirt','records-t-shirt','choices-say-no-to-viagra-t-shirt','take-care-bear-t-shirt',
    'universal-programming-t-shirt','choices-shattered-backboard-t-shirt'
  ]);

  const sizeOnly = {
    'clint-knows-t-shirt': ['S','M','L','XL','2XL'],
    'dont-say-the-enwurd-t-shirt': ['S','M','L','XL','2XL','3XL'],
    'magic-photo-t-shirt': ['S','M','L','XL','2XL'],
    'no-glaze-t-shirt': ['S','M','L','XL','2XL','3XL']
  };

  const hatHandles = new Set([
    'choices-cape-red-camo-trucker-hat','choices-cape-trucker-hat','choices-dodge-this-5-panel-hat'
  ]);

  const standardRows = [
    ['XS','28.35 in / 72 cm','21.54 in / 54.7 cm','21.85 in / 55.5 cm','9.02 in / 22.9 cm'],
    ['S','29.13 in / 74 cm','22.13 in / 56.2 cm','22.64 in / 57.5 cm','9.21 in / 23.4 cm'],
    ['M','29.92 in / 76 cm','22.72 in / 57.7 cm','23.43 in / 59.5 cm','9.41 in / 23.9 cm'],
    ['L','30.71 in / 78 cm','23.43 in / 59.5 cm','24.41 in / 62 cm','9.65 in / 24.5 cm'],
    ['XL','31.50 in / 80 cm','24.13 in / 61.3 cm','25.39 in / 64.5 cm','9.88 in / 25.1 cm'],
    ['XXL','32.28 in / 82 cm','24.84 in / 63.1 cm','26.38 in / 67 cm','10.12 in / 25.7 cm']
  ];

  function currentHandle() {
    const match = location.pathname.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  }

  function table(headers, rows) {
    return `<div class="choices-size-guide__scroll"><table class="choices-size-guide__table"><thead><tr>${headers.map((h) => `<th>${h}</th>`).join('')}</tr></thead><tbody>${rows.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`;
  }

  function guideFor(handle) {
    if (standardHandles.has(handle)) {
      return `<section class="choices-size-guide" data-choices-size-guide><h3>Size guide</h3>${table(['Size','Length','Shoulder','Chest','Sleeve'], standardRows)}<p class="choices-size-guide__note"><strong>Garment:</strong> R00227 · Unisex · 100% cotton · 9 oz/yd² (305 g/m²) · slight stretch.</p></section>`;
    }
    if (sizeOnly[handle]) {
      const rows = sizeOnly[handle].map((size) => [size, 'Available']);
      return `<section class="choices-size-guide" data-choices-size-guide><h3>Size guide</h3>${table(['Size','Availability'], rows)}<p class="choices-size-guide__note">Select your usual size for the intended oversized streetwear fit. Exact garment measurements vary by this blank.</p></section>`;
    }
    if (hatHandles.has(handle)) {
      return `<section class="choices-size-guide" data-choices-size-guide><h3>Size guide</h3>${table(['Size','Fit'], [['One size','Adjustable snap closure']])}<p class="choices-size-guide__note">The adjustable closure is designed to fit most adults.</p></section>`;
    }
    return '';
  }

  function addStyles() {
    if (document.getElementById('choices-size-guide-styles')) return;
    const style = document.createElement('style');
    style.id = 'choices-size-guide-styles';
    style.textContent = `.choices-size-guide{margin-top:2.4rem;padding-top:2rem;border-top:1px solid rgba(var(--color-foreground),.15)}.choices-size-guide h3{margin:0 0 1rem}.choices-size-guide__scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}.choices-size-guide__table{width:100%;min-width:580px;border-collapse:collapse}.choices-size-guide__table th,.choices-size-guide__table td{padding:.9rem 1rem;border:1px solid rgba(var(--color-foreground),.18);text-align:left;white-space:nowrap}.choices-size-guide__table th{font-weight:700;background:rgba(var(--color-foreground),.05)}.choices-size-guide__note{margin:1rem 0 0;font-size:1.35rem;line-height:1.5}`;
    document.head.appendChild(style);
  }

  function mount(root = document) {
    const handle = currentHandle();
    if (!handle) return;
    const description = root.querySelector?.('.product__description') || document.querySelector('.product__description');
    if (!description || description.querySelector('#size-guide, .size-guide-title, [data-choices-size-guide]')) return;
    const html = guideFor(handle);
    if (!html) return;
    addStyles();
    description.insertAdjacentHTML('beforeend', html);
  }

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(), { once: true });
  else mount();
})();
